/*
 * DialUpDuo motion preference.
 *
 * Gives visitors an in-page way to stop the decorative animation (peeking
 * pets, the True Crime page intro, arcade flourishes) rather than making them
 * find an OS setting. This is what WCAG 2.2.2 "Pause, Stop, Hide" actually
 * asks for: a mechanism in the content, not a device preference.
 *
 * Storage contract, deliberately the same shape as duo-theme.js:
 *   localStorage['duo:motion:v1'] holds 'full' or 'reduced', and ONLY after an
 *   explicit choice. Absent means "follow the device", so a visitor who has
 *   prefers-reduced-motion set at the OS level still gets a calm site by
 *   default and never has to touch this. An explicit choice wins over the OS,
 *   in both directions, because an intentional click is a stronger signal than
 *   a system default.
 *
 * The resolved state lives on html[data-motion="full"|"reduced"], mirroring
 * html[data-theme]. That keeps CSS a plain attribute selector and lets the
 * inline boot script in layout/theme.liquid settle it before first paint, so
 * nothing animates for one frame and then stops.
 *
 * Guest-first, no identifiers, storage failures are non-fatal (Safari private
 * mode), same guard pattern as duo-theme.js and duo-wishlist.js.
 */
(() => {
  'use strict';

  const KEY = 'duo:motion:v1';
  const root = document.documentElement;
  const media = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  let memoryChoice = null; // fallback when storage is blocked

  function readChoice() {
    let value = memoryChoice;
    try {
      value = localStorage.getItem(KEY) || memoryChoice;
    } catch (error) {
      /* storage blocked: memoryChoice carries the session */
    }
    return value === 'full' || value === 'reduced' ? value : null;
  }

  function writeChoice(choice) {
    memoryChoice = choice;
    try {
      if (choice === null) localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, choice);
    } catch (error) {
      /* storage blocked: session-only preference still works */
    }
  }

  function systemReduced() {
    return !!(media && media.matches);
  }

  function resolve() {
    const choice = readChoice();
    if (choice) return choice;
    return systemReduced() ? 'reduced' : 'full';
  }

  function apply(state) {
    root.dataset.motion = state;
    document.querySelectorAll('[data-duo-motion-switch]').forEach((el) => {
      el.setAttribute('aria-checked', state === 'full' ? 'true' : 'false');
    });
    document.dispatchEvent(new CustomEvent('duo:motion:change', { detail: { motion: state } }));
  }

  const api = {
    get current() {
      return root.dataset.motion === 'reduced' ? 'reduced' : 'full';
    },
    get choice() {
      return readChoice();
    },
    reduced() {
      return this.current === 'reduced';
    },
    set(state) {
      // 'full' | 'reduced' store an explicit override; null returns to the device setting.
      writeChoice(state === 'full' || state === 'reduced' ? state : null);
      apply(resolve());
    },
    toggle() {
      this.set(this.current === 'full' ? 'reduced' : 'full');
    },
  };

  window.duoMotion = api;

  // Follow the OS live, but only while the visitor has not made a choice.
  if (media) {
    const onSystemChange = () => {
      if (!readChoice()) apply(resolve());
    };
    if (typeof media.addEventListener === 'function') media.addEventListener('change', onSystemChange);
    else if (typeof media.addListener === 'function') media.addListener(onSystemChange);
  }

  // The boot script already set data-motion; re-apply so listeners and the
  // switch's aria-checked settle even if this loads after the panel renders.
  apply(resolve());
})();
